import { useSpring, animated, easings } from "@react-spring/web";
import truthPlusFilledIcon from "@tabler/icons/filled/truth-plus.svg";
import checkIcon from "@tabler/icons/outline/check.svg";
import truthPlusOutlineIcon from "@tabler/icons/outline/truth-plus.svg";
import xIcon from "@tabler/icons/outline/x.svg";
import React, { useEffect, useState } from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";

import amexImage from "@/assets/images/payment/amex.png";
import cardDiscoverImage from "@/assets/images/payment/discover.png";
import mastercardImage from "@/assets/images/payment/mastercard.png";
import visaImage from "@/assets/images/payment/visa.png";
import { usePremium } from "@/hooks";
import { config } from "@/utils/config";

import SidebarNavigationLink from "../sidebar/sidebar-navigation-link";
import { Button, HStack, Icon, IconButton, Portal, Stack, Text } from "../ui";

import FeatureTable from "./feature-table";

const messages = defineMessages({
  close: { id: "common.close", defaultMessage: "Close" },
  title: {
    id: "premium.subscription_dialog.title",
    defaultMessage: "Get more with Truth+",
  },
  subtitle: {
    id: "premium.subscription_dialog.subtitle",
    defaultMessage:
      "Non-woke movies, live news channels, Christian content, and more. All available on iOS, Android, the web, and connected TVs.",
  },
  premiumButton: {
    id: "premium.subscription_dialog.premium_button",
    defaultMessage: "Get the Truth+ Patriot Package",
  },
  basic: { id: "premium.subscription_dialog.basic", defaultMessage: "Basic" },
  premium: {
    id: "premium.subscription_dialog.premium",
    defaultMessage: "Patriot",
  },
  costTitle: {
    id: "premium.subscription_dialog.cost_title",
    defaultMessage: "Major value at a minor cost",
  },
  costSubtitle: {
    id: "premium.subscription_dialog.cost_subtitle",
    defaultMessage:
      "Choose our Patriot Package subscription to get verified, access additional features on Truth Social, and watch premium live and on-demand video content. Cancel anytime.",
  },
  costCaveat: {
    id: "premium.subscription_dialog.cost_caveat",
    defaultMessage: "Currently available only in the U.S.",
  },
  basicPackage: {
    id: "premium.subscription_dialog.basic_package",
    defaultMessage: "Basic",
  },
  patriotPackage: {
    id: "premium.subscription_dialog.patriot_package",
    defaultMessage: "Patriot Package",
  },
  free: { id: "premium.subscription_dialog.free", defaultMessage: "Free" },
  patriotPackageSubtitle: {
    id: "premium.subscription_dialog.patriot_package_subtitle",
    defaultMessage: "Get the first 7 days free, then",
  },
  patriotPackageIncluded: {
    id: "premium.subscription_dialog.patriot_package_included",
    defaultMessage: "Everything included in the Basic package, plus:",
  },
  month: { id: "premium.subscription_dialog.month", defaultMessage: "month" },
});

const cards = [
  { name: "Visa", src: visaImage },
  { name: "Mastercard", src: mastercardImage },
  { name: "American Express", src: amexImage },
  { name: "Discover", src: cardDiscoverImage },
];

const freePackageFeatures = [
  <FormattedMessage
    id="premium.subscription_dialog.free_package.channels"
    defaultMessage="21 channels, 1,000+ shows, movies, and documentaries"
  />,
  <FormattedMessage
    id="premium.subscription_dialog.free_package.live_tv"
    defaultMessage="Watch 24/7 live TV from the news networks you trust"
  />,
  <FormattedMessage
    id="premium.subscription_dialog.free_package.stream_shows"
    defaultMessage="Stream the shows and movies that Hollywood tries to silence"
  />,
  <FormattedMessage
    id="premium.subscription_dialog.free_package.dvr"
    defaultMessage="Build your content library with 7-day catch up DVR"
  />,
  <FormattedMessage
    id="premium.subscription_dialog.free_package.access"
    defaultMessage="Easy access from your computer, smartphone, tablet, or smart TV"
  />,
  <FormattedMessage
    id="premium.subscription_dialog.free_package.stream_devices"
    defaultMessage="Stream on up to 3 devices"
  />,
  <FormattedMessage
    id="premium.subscription_dialog.free_package.storage"
    defaultMessage="10 GB of storage"
  />,
];

const patriotPackageFeatures = [
  <FormattedMessage
    id="premium.subscription_dialog.patriot_package.premium_content"
    defaultMessage="Premium content from non-woke news networks such as Newsmax and One American News"
  />,
  <FormattedMessage
    id="premium.subscription_dialog.patriot_package.streaming"
    defaultMessage="Premier on-demand streaming from Great American Channels, Curiosity, and more"
  />,
  <FormattedMessage
    id="premium.subscription_dialog.patriot_package.verification"
    defaultMessage="Red check verification for your Truth Social account"
  />,
  <FormattedMessage
    id="premium.subscription_dialog.patriot_package.badge"
    defaultMessage="Truth+ badge for your Truth Social account"
  />,
  <FormattedMessage
    id="premium.subscription_dialog.patriot_package.boosted"
    defaultMessage="Access to Truth Gems, edit and schedule Truths, and boosted comments"
  />,
];

export default function SubscriptionDialog() {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const { hasPremium } = usePremium();

  const styles = useSpring({
    opacity: isOpen ? 1 : 0,
    transform: isOpen ? "translateY(0%)" : "translateY(10%)",
    config: {
      duration: 200,
      easing: easings.easeInOutCirc,
    },
  });

  function handleClickLink() {
    if (hasPremium) {
      window.open(config.truthPlusPlayerLink, "_blank");
    } else {
      setIsOpen(true);
    }
  }

  function handleClose() {
    setIsOpen(false);
  }

  function handleKeyUp(event: KeyboardEvent) {
    if (event.key === "Escape" || event.key === "Esc" || event.keyCode === 27) {
      handleClose();
    }
  }

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      window.addEventListener("keyup", handleKeyUp, false);
    }

    return () => {
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [isOpen]);

  return (
    <>
      <SidebarNavigationLink
        onClick={handleClickLink}
        icon={truthPlusOutlineIcon}
        activeIcon={truthPlusFilledIcon}
        text={
          <FormattedMessage id="tabs_bar.truth_plus" defaultMessage="Truth+" />
        }
      />

      <Portal>
        <animated.div
          style={styles}
          className={`fixed inset-0 z-1100 overflow-y-auto bg-primary-200/60 backdrop-blur-3xl transition-all dark:bg-gray-900/60 ${
            isOpen ? "pointer-events-auto" : "pointer-events-none"
          }`}
        >
          <IconButton
            src={xIcon}
            title={intl.formatMessage(messages.close)}
            onClick={handleClose}
            className="absolute top-2 left-2 text-gray-900 dark:text-white"
            data-testid="close-modal"
          />

          <Stack
            justifyContent="center"
            alignItems="center"
            className="mx-auto max-w-4xl space-y-28 px-4 py-32 lg:px-0"
          >
            <Stack
              justifyContent="center"
              alignItems="center"
              space={4}
              className="mx-auto max-w-2xl"
            >
              <Text
                weight="bold"
                align="center"
                className="text-3xl! lg:text-5xl!"
              >
                {intl.formatMessage(messages.title)}
              </Text>

              <Text
                align="center"
                theme="muted"
                className="text-lg! lg:text-xl!"
              >
                {intl.formatMessage(messages.subtitle)}
              </Text>

              <Button
                variant={null}
                className="border-transparent bg-accent-green text-gray-900 hover:bg-accent-green/90"
                to="/settings/subscriptions"
                onClick={handleClose}
              >
                {intl.formatMessage(messages.premiumButton)}
              </Button>
            </Stack>

            <FeatureTable tableClassName="mx-auto w-full max-w-3xl" size="lg" />

            <Stack
              justifyContent="center"
              alignItems="center"
              space={9}
              className="mx-auto w-full max-w-3xl"
            >
              <Stack justifyContent="center" alignItems="center" space={4}>
                <Text
                  weight="bold"
                  align="center"
                  className="text-xl! lg:text-3xl!"
                >
                  {intl.formatMessage(messages.costTitle)}
                </Text>

                <Text
                  align="center"
                  theme="muted"
                  className="text-lg! lg:text-xl!"
                >
                  {intl.formatMessage(messages.costSubtitle)}
                </Text>

                <Text
                  align="center"
                  theme="muted"
                  weight="bold"
                  className="text-lg! lg:text-xl!"
                >
                  {intl.formatMessage(messages.costCaveat)}
                </Text>
              </Stack>

              <div className="grid grid-cols-4 gap-4">
                {cards.map((card) => (
                  <img
                    key={card.name}
                    src={card.src}
                    alt={card.name}
                    className="h-10 w-16 lg:h-16 lg:w-28"
                  />
                ))}
              </div>
            </Stack>

            <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2 md:gap-16">
              <Stack
                space={4}
                className="w-full rounded-2xl border border-solid border-white p-6 dark:border-gray-800"
              >
                <Stack space={2}>
                  <Text
                    theme="primary"
                    weight="bold"
                    align="center"
                    transform="uppercase"
                    tracking="wide"
                    size="sm"
                  >
                    {intl.formatMessage(messages.basicPackage)}
                  </Text>
                  <Text size="3xl" weight="bold" align="center">
                    {intl.formatMessage(messages.free)}
                  </Text>
                </Stack>

                <Stack space={3}>
                  {freePackageFeatures.map((feature, index) => (
                    <HStack key={index} space={2} alignItems="center">
                      <Icon
                        src={checkIcon}
                        className="size-6 text-primary-500 dark:text-primary-400"
                      />

                      <Text size="sm">{feature}</Text>
                    </HStack>
                  ))}
                </Stack>
              </Stack>

              <Stack
                space={4}
                className="w-full rounded-2xl border border-solid border-white p-6 dark:border-gray-800"
              >
                <Stack space={2}>
                  <Text
                    theme="primary"
                    weight="bold"
                    align="center"
                    transform="uppercase"
                    tracking="wide"
                    size="sm"
                  >
                    {intl.formatMessage(messages.patriotPackage)}
                  </Text>

                  <Text size="sm" weight="medium" align="center">
                    {intl.formatMessage(messages.patriotPackageSubtitle)}
                  </Text>

                  <HStack
                    alignItems="bottom"
                    space={1.5}
                    justifyContent="center"
                  >
                    <Text size="3xl" weight="bold">
                      $9.99
                    </Text>
                    <Text size="lg" weight="medium" theme="muted">
                      / {intl.formatMessage(messages.month)}
                    </Text>
                  </HStack>
                </Stack>

                <Stack space={3} grow>
                  <Text size="sm" weight="bold">
                    {intl.formatMessage(messages.patriotPackageIncluded)}
                  </Text>

                  {patriotPackageFeatures.map((feature, index) => (
                    <HStack key={index} space={2} alignItems="center">
                      <Icon
                        src={checkIcon}
                        className="size-6 text-primary-500 dark:text-primary-400"
                      />

                      <Text size="sm">{feature}</Text>
                    </HStack>
                  ))}
                </Stack>

                <Button
                  className="mx-auto border-transparent bg-accent-green text-gray-900 hover:bg-accent-green/90"
                  variant={null}
                  to="/settings/subscriptions"
                  onClick={handleClose}
                >
                  {intl.formatMessage(messages.premiumButton)}
                </Button>
              </Stack>
            </div>
          </Stack>
        </animated.div>
      </Portal>
    </>
  );
}
